import React, { useEffect, useState } from 'react';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { Receipt, Dashboard, Timer, Person, ExitToApp, Home, Payment } from '@material-ui/icons';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import ResponsiveDrawer from '../../ResponsiveDrawer';
import { Box, CssBaseline } from '@material-ui/core';
import { Link, Redirect } from 'react-router-dom'
import request, {
  clearTeacherImpersonation,
  getConfigSync,
  getTeacherImpersonation,
  setTeacherImpersonation,
  TeacherImpersonationInfo,
} from '../../api';
import TeacherImpersonationBanner from '../../components/TeacherImpersonationBanner';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    toolbar: theme.mixins.toolbar,
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
    }
  })
);

interface Props {
  heading:string
  children:any
}

export default function TeacherContainer(props: Props) {
  const classes = useStyles();
  const [navigate, setNavigate] = useState(false);
  const [navigateHome, setNavigateHome] = useState(false);
  const [returnToExaminer, setReturnToExaminer] = useState(false);
  const [impersonation, setImpersonation] = useState<TeacherImpersonationInfo | null>(
    () => getTeacherImpersonation()
  );

  useEffect(() => {
    let cancelled = false

    async function loadSession() {
      try {
        const res = await request('/api/teacher/session')
        if (!res.ok || cancelled) return

        const body = await res.json()
        if (body.impersonation) {
          setTeacherImpersonation(body.impersonation)
          setImpersonation(body.impersonation)
        } else {
          clearTeacherImpersonation()
          setImpersonation(null)
        }
      } catch (_) {
        // Keep cached impersonation banner if session check fails transiently.
      }
    }

    loadSession()
    return () => { cancelled = true }
  }, [])

  if (navigate) {
    request('/api/teacher/logout/', { method: 'DELETE' })
    sessionStorage.removeItem(`ukbc_teacher`)
    clearTeacherImpersonation()
    return <Redirect to="/login" push={true} />
  }

  if (returnToExaminer) {
    request('/api/teacher/logout/', { method: 'DELETE' })
    sessionStorage.removeItem(`ukbc_teacher`)
    const teacherId = impersonation?.teacherId
    clearTeacherImpersonation()
    window.location.href = teacherId ? `/examiner/teachers/${teacherId}` : '/examiner/teachers'
    return null
  }

  if (navigateHome) {
    window.location.href=getConfigSync().competitionHomepage
  }

  const drawer = (
    <div>
      <img className='centreImage' src={getConfigSync().logo} width={150} alt={`${getConfigSync().competitionShortName} logo`}/>
      <Divider />
      <List>
        <ListItem button component={Link} to="/teacher">
          <ListItemIcon> <Dashboard /> </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem button component={Link} to="/teacher/updateDetails">
          <ListItemIcon> <Person /> </ListItemIcon>
          <ListItemText primary="Update Details" />
        </ListItem>
        <ListItem button component={Link} to="/teacher/exams">
          <ListItemIcon> <Receipt /> </ListItemIcon>
          <ListItemText primary="Manage Competitions" />
        </ListItem>
        <ListItem button component={Link} to="/teacher/sittings">
          <ListItemIcon> <Timer /> </ListItemIcon>
          <ListItemText primary="Invigilation" />
        </ListItem>
        <ListItem button component={Link} to="/teacher/payments">
          <ListItemIcon> <Payment /> </ListItemIcon>
          <ListItemText primary="Payments" />
        </ListItem>
        <ListItem button onClick={()=> setNavigateHome(true)}>
          <ListItemIcon> <Home /> </ListItemIcon>
          <ListItemText primary={`${getConfigSync().competitionShortName} Home`} />
        </ListItem>
        <ListItem button onClick={()=> setNavigate(true)}>
          <ListItemIcon> <ExitToApp /> </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </div>
  );

  return <div className={classes.root}>
    <CssBaseline/>
    <ResponsiveDrawer heading={props.heading}>
      {drawer}
    </ResponsiveDrawer>

    <main className={classes.content}>
      <div className={classes.toolbar} />
      <Box>
        {impersonation && (
          <TeacherImpersonationBanner
            impersonation={impersonation}
            onReturnToExaminer={() => setReturnToExaminer(true)}
          />
        )}
        {props.children}
      </Box>
    </main>
  </div>
}
